import React, { createContext, useCallback, useContext, useRef, useState, useEffect, } from 'react';
import clsx from 'clsx';
import DeviceManager, { DeviceManagerContext, } from '@trainline-shareable/device-manager';
import Depot from '../../_shared/depot';
import * as styles from './style.module.css';
import Header from './Header';
import Content from './Content';
import Footer from './Footer';
import Dialog from '../shared/Dialog';
import { isDialogSupported } from '../shared/helpers';
export const modalPrefix = 'modal';
export const modalSizes = ['small', 'medium', 'large'];
const ModalSubComponents = {
    Content,
    Header,
    Footer,
};
const mobileModalOptionsDefaultValues = {
    size: 'small',
    resizable: true,
};
export const ModalParentWrapperContext = createContext({
    mobileModalOptions: mobileModalOptionsDefaultValues,
});
const ModalWithoutRef = ({ modalState, desktopModalSize = 'small', mobileModalOptions = mobileModalOptionsDefaultValues, dismissible = true, stopPropagation = true, closeModalHandler, children, id, className, onClick, onKeyDown, alwaysRenderContents, ...commonProps }) => {
    const { size: mobileModalSize, resizable } = mobileModalOptions;
    const [headingId, setHeadingId] = useState();
    // Refs
    const dialogRef = useRef(null);
    const mobileModalContainerRef = useRef(null);
    // Contexts
    const deviceManager = useContext(DeviceManagerContext);
    const isMobileViewport = /(X?S)/.test((deviceManager === null || deviceManager === void 0 ? void 0 : deviceManager.viewPort) || 'S');
    const sharedClasses = clsx(isMobileViewport ? styles.mobile : styles.desktop, isMobileViewport && styles[mobileModalSize], {
        [styles.opening]: modalState === 'opening',
        [styles.closing]: modalState === 'closing',
    });
    const dialogClasses = clsx(styles.dialog, sharedClasses, className);
    const modalClasses = clsx(styles.modal, sharedClasses, !isMobileViewport && styles[desktopModalSize], {
        [styles.notResizable]: !resizable,
    });
    const desktopDialogClasses = clsx(dialogClasses, modalClasses);
    const mobileModalContainerClasses = clsx(styles.mobileModalContainer, sharedClasses);
    // Event Handlers
    const dialogOnKeyDown = useCallback((event) => {
        if (stopPropagation) {
            event.stopPropagation();
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
        if (dismissible &&
            closeModalHandler &&
            modalState === 'open' &&
            event.key === 'Escape') {
            event.preventDefault();
            closeModalHandler();
        }
    }, [closeModalHandler, dismissible, modalState, stopPropagation, onKeyDown]);
    const clickOutsideToClose = useCallback((event) => {
        if (dismissible &&
            closeModalHandler &&
            modalState === 'open' &&
            (event.target === event.currentTarget ||
                event.target === mobileModalContainerRef.current)) {
            event.preventDefault();
            closeModalHandler();
        }
    }, [closeModalHandler, dismissible, modalState]);
    const dialogOnClick = useCallback((event) => {
        if (stopPropagation) {
            event.stopPropagation();
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
        clickOutsideToClose(event);
    }, [clickOutsideToClose, onClick, stopPropagation]);
    const clickOutsideToCloseDialogFallback = useCallback((event) => {
        var _a;
        if (dismissible &&
            closeModalHandler &&
            modalState === 'open' &&
            !((_a = dialogRef === null || dialogRef === void 0 ? void 0 : dialogRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            event.preventDefault();
            closeModalHandler();
        }
    }, [closeModalHandler, dismissible, modalState, dialogRef]);
    useEffect(() => {
        if (!isDialogSupported && modalState === 'open') {
            document.addEventListener('click', clickOutsideToCloseDialogFallback);
        }
        return () => {
            if (!isDialogSupported) {
                document.removeEventListener('click', clickOutsideToCloseDialogFallback);
            }
        };
    }, [modalState, clickOutsideToCloseDialogFallback]);
    if (!alwaysRenderContents && modalState === 'closed') {
        return null;
    }
    const isOpen = !alwaysRenderContents || modalState !== 'closed';
    return (React.createElement(ModalParentWrapperContext.Provider, { value: {
            mobileModalOptions,
            setHeadingId,
        } }, isMobileViewport ? (React.createElement(Dialog, { ...commonProps, trapFocus: !!dismissible, className: dialogClasses, isOpen: isOpen, "aria-modal": true, "aria-labelledby": headingId, onClick: dialogOnClick, onKeyDown: dialogOnKeyDown, ref: dialogRef },
        React.createElement("div", { className: mobileModalContainerClasses, ref: mobileModalContainerRef, "data-testid": "mobileModalContainer" },
            React.createElement("div", { className: modalClasses }, children)))) : (React.createElement(Dialog, { ...commonProps, trapFocus: !!dismissible, isOpen: isOpen, className: desktopDialogClasses, "aria-modal": true, "aria-labelledby": headingId, onClick: dialogOnClick, onKeyDown: dialogOnKeyDown, ref: dialogRef }, children))));
};
const ModalContext = (props) => {
    return (React.createElement(DeviceManager, null,
        React.createElement(ModalWithoutRef, { ...props })));
};
ModalContext.Header = Header;
ModalContext.Content = Content;
ModalContext.Footer = Footer;
const Modal = Depot.withRef(ModalContext, ModalSubComponents);
Modal.displayName = 'Modal';
export default Modal;
//# sourceMappingURL=index.js.map